import {defineStore} from 'pinia'
import {ref, computed} from 'vue'
import {GlobalSocketEventType} from '@toolify/server/src/services/SocketService/enum/GlobalSocketEventType'
import {useSocketStore} from '@toolify/client/src/stores/SocketStore/useSocketStore'
import {JobType} from '@toolify/server/src/jobs/enum/JobType'
import {IJobInterfacesByJobTypeMap} from '@toolify/server/src/services/JobQueueScheduler/types/IJobInterfacesByJobTypeMap'
import {IJobProgressUpdatedPayload} from '@toolify/server/src/services/JobQueueScheduler/types/IJobProgressUpdatedPayload'

export interface IJobProgressState {
  jobId: string
  jobType: JobType | null
  progress: IJobInterfacesByJobTypeMap[JobType]['progress'] | null
  completedAt: string | null
  failedAt: string | null
}

interface IJobProgressUpdatedPayloadClient extends Omit<IJobProgressUpdatedPayload, 'completedAt' | 'failedAt'> {
  completedAt: string | null
  failedAt: string | null
}

interface IJobCompletionWaiter {
  resolve: () => void
  reject: (error: Error) => void
}

export const JOB_SUBSCRIPTION_CANCELLED_ERROR = 'Job subscription cancelled'

export const useJobProgressStore = defineStore('jobProgress', () => {
  const socketStore = useSocketStore()
  const stateByJobId = ref<Map<string, IJobProgressState>>(new Map())
  const subscriberCountByJobId = new Map<string, number>()
  const completionWaitersByJobId = new Map<string, IJobCompletionWaiter[]>()
  let progressListener: ((payload: IJobProgressUpdatedPayloadClient) => void) | null = null
  let connectListener: (() => void) | null = null

  function handleProgress(payload: IJobProgressUpdatedPayloadClient) {
    if(!subscriberCountByJobId.get(payload.jobId)) return
    const next: IJobProgressState = {
      jobId: payload.jobId,
      jobType: payload.jobType ?? null,
      progress: payload.progress,
      completedAt: payload.completedAt,
      failedAt: payload.failedAt,
    }
    const map = new Map(stateByJobId.value)
    map.set(payload.jobId, next)
    stateByJobId.value = map

    const waiters = completionWaitersByJobId.get(payload.jobId)
    if(!waiters) return
    if(payload.failedAt) {
      for(const waiter of waiters) waiter.reject(new Error('Job failed'))
      completionWaitersByJobId.delete(payload.jobId)
      return
    }
    if(payload.completedAt) {
      for(const waiter of waiters) waiter.resolve()
      completionWaitersByJobId.delete(payload.jobId)
    }
  }

  function handleReconnect() {
    for(const [jobId, count] of subscriberCountByJobId.entries()) {
      if(count > 0) {
        socketStore.socket?.emit(GlobalSocketEventType.SubscribeJob, {jobId})
      }
    }
  }

  function ensureListeners() {
    if(progressListener) return
    progressListener = handleProgress
    socketStore.on(GlobalSocketEventType.JobProgressUpdated, progressListener)
    connectListener = handleReconnect
    socketStore.on('connect', connectListener)
  }

  function disposeListeners() {
    if(progressListener) {
      socketStore.off(GlobalSocketEventType.JobProgressUpdated, progressListener)
      progressListener = null
    }
    if(connectListener) {
      socketStore.off('connect', connectListener)
      connectListener = null
    }
  }

  function subscribe(jobId: string) {
    ensureListeners()
    const count = subscriberCountByJobId.get(jobId) ?? 0
    subscriberCountByJobId.set(jobId, count + 1)
    if(count === 0) {
      socketStore.socket?.emit(GlobalSocketEventType.SubscribeJob, {jobId})
    }
  }

  function unsubscribe(jobId: string) {
    const count = subscriberCountByJobId.get(jobId) ?? 0
    if(count === 0) return
    if(count <= 1) {
      subscriberCountByJobId.delete(jobId)
      socketStore.socket?.emit(GlobalSocketEventType.UnsubscribeJob, {jobId})
      const map = new Map(stateByJobId.value)
      map.delete(jobId)
      stateByJobId.value = map
      const waiters = completionWaitersByJobId.get(jobId)
      if(waiters) {
        for(const waiter of waiters) waiter.reject(new Error(JOB_SUBSCRIPTION_CANCELLED_ERROR))
        completionWaitersByJobId.delete(jobId)
      }
      return
    }
    subscriberCountByJobId.set(jobId, count - 1)
  }

  function getState(jobId: string) {
    return computed(() => stateByJobId.value.get(jobId) ?? null)
  }

  function awaitCompletion(jobId: string): Promise<void> {
    if(!subscriberCountByJobId.get(jobId)) {
      return Promise.reject(new Error('awaitCompletion called without active subscription'))
    }
    const existing = stateByJobId.value.get(jobId)
    if(existing?.failedAt) return Promise.reject(new Error('Job failed'))
    if(existing?.completedAt) return Promise.resolve()
    return new Promise((resolve, reject) => {
      const waiters = completionWaitersByJobId.get(jobId) ?? []
      waiters.push({
        resolve,
        reject,
      })
      completionWaitersByJobId.set(jobId, waiters)
    })
  }

  return {
    subscribe,
    unsubscribe,
    getState,
    awaitCompletion,
    dispose: disposeListeners,
  }
})
